import React, { useState } from 'react';
import dayjs from 'dayjs';
import { SwiperSlide } from 'swiper/react';

import { Button } from '$partials/ui';
import { Carousel, CarouselArrowNavigation, CarouselWrapper } from '$partials/base/carousel';
import { Footer, Header } from '$partials/global';
import { Media } from '$partials/media';
import { ModuleDispatcher } from '$partials/modules';
import ArticleListingItem from '$partials/items/article-listing-item';
import ShareButtons from '$partials/global/share-buttons';
import { IconMic } from '$partials/icons';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

const ArticleSidebar = ({page}) => {
    const [showTags, setShowTags] = useState(false);

    const formatter = new Intl.ListFormat(
        'en',
        {
            type: 'conjunction',
            style: 'long'
        }
    );

    return (
        <aside className='sm:sticky top-0 flex flex-col'>
            <div className='py-16 px-8 md:p-16'>
                <p className='type-style-single-line-eyebrow mb-8'>
                    {'Author'}
                </p>
                <p className='type-style-sans-body'>
                    {formatter.format(page?.authors?.map(el => el.title))}
                </p>
            </div>
            <div className='py-16 px-8 md:p-16 border-t border-border-default'>
                <p className='type-style-single-line-eyebrow mb-8'>
                    {'Date'}
                </p>
                <p className='type-style-sans-body'>
                    {dayjs(page.publicationDate).format('DD MMMM YYYY')}
                </p>
            </div>
            {page.tags?.length > 0 && (
                <div className='py-16 px-8 md:p-16 border-t border-border-default'>
                    <p className='type-style-single-line-eyebrow mb-8'>
                        {'Tags'}
                    </p>
                    <div className='flex flex-wrap gap-8'>
                        {page.tags.map((tag, idx) => {
                            if (!showTags && idx > 5) {
                                return null;
                            }
                            return (
                                <Button
                                    as='a'
                                    className='button-outcome'
                                    href={tag.url}
                                    key={tag.id}>
                                    {tag.title}
                                </Button>
                            );
                        })}
                        {!showTags && page.tags.length > 6 && (
                            <Button
                                as='button'
                                className='button-outcome'
                                onClick={setShowTags.bind(null, true)}>
                                {'...'}
                            </Button>
                        )}
                    </div>
                </div>
            )}
            <div className='py-16 px-8 md:p-16 border-t border-border-default'>
                <p className='type-style-single-line-eyebrow mb-8'>
                    {'Share'}
                </p>
                <ShareButtons />

                {page?.relatedMagazine && (
                    <div className='sm:max-w-[411px] rounded-listing-item bg-surface-canvas pt-16 sm:pt-24 pb-8 sm:pb-16 mt-16 flex flex-col relative'>
                        <div className='px-16 z-10'>
                            <p className='type-style-sans-h3-med'>
                                {page.relatedMagazine.relatedArticleHeading}
                            </p>
                            {page.relatedMagazine.relatedArticleText && (
                                <p className='type-style-sans-body mt-8'>
                                    {page.relatedMagazine.relatedArticleText}
                                </p>
                            )}
                            <Button
                                as='a'
                                className={`${page.relatedMagazine.relatedArticleImage ? 'md:mb-[200px] ' : ''}button-primary mt-32 self-start`}
                                href={page.relatedMagazine.parent.url}>
                                {'Find out more'}
                            </Button>
                        </div>
                        {page.relatedMagazine.relatedArticleImage && (
                            <div
                                className='absolute hidden md:block top-0 w-full h-full rounded-lg overflow-hidden bg-cover bg-no-repeat z-0 mix-blend-multiply'
                                style={{
                                    backgroundImage: `url(${page.relatedMagazine.relatedArticleImage.src})`,
                                }}>
                            </div>
                        )}
                    </div>
                )}
            </div>

        </aside>
    );
};

/**
 * @type {import('$types/components/pages.d.ts').ArticleDetailPage}
 */
const ArticleDetailPage = (props) => {
    const page = props.data.page;
    return (
        <>
            <Header
                {...props?.data?.header}
                pageType={page.__typename} />

            <main id='main-content'>
                <div className='layout-container layout-site-outer-padding pb-8 md:pb-16'>
                    {/* Main Content */}
                    <div className='col-wrapper-breakout flex flex-wrap rounded-lg bg-surface-panel-red'>
                        {/* Left Col */}
                        <div className='w-full sm:w-9/12 md:w-8/12 flex flex-col gap-24 py-16 sm:border-r border-border-default'>
                            <section className='flex flex-col gap-16 sm:gap-32'>
                                {/* Type */}
                                <div className='col-padding'>
                                    <ul className='flex flex-wrap gap-4'>
                                        {page?.articleTypes?.map((itm, idx) => {
                                            return (
                                                <li key={idx}>
                                                    <a
                                                        className={idx === 0 ? 'tag-primary' : 'tag-outcome'}
                                                        href={`${page?.parent?.url}?articleType=${itm.slug}`}>
                                                        <span>{itm.name}</span>

                                                        {itm.slug === 'podcasts' && <IconMic />}
                                                    </a>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>

                                <div className='flex flex-col gap-8 col-padding'>
                                    {/* Title */}
                                    <h1 className='type-style-sans-display'>
                                        {page.title}
                                    </h1>

                                    {page.articleStandfirst && (
                                        <h2 className='type-style-serif-h3 w-full sm:pr-56 md:pr-0 md:w-3/4'>
                                            {page.articleStandfirst}
                                        </h2>
                                    )}

                                    {page?.podcastEmbed && (
                                        <figure className='podcast-embed mt-24'>
                                            <iframe
                                                allow='autoplay'
                                                frameBorder='0'
                                                loading='lazy'
                                                src={page.podcastEmbed} />
                                        </figure>
                                    )}
                                </div>

                                {/* Media Asset */}
                                {page.heroMedia && (
                                    <div>
                                        <figure className='col-padding'>
                                            <Media {...{...page.heroMedia, image: { ...page.heroMedia?.image, containerClassName:'rounded-lg overflow-hidden pointer-events-none select-none' }}}/>
                                            {page.heroMediaCaption && (
                                                <figcaption className='mt-8 type-style-sans-body text-text-default-secondary'>
                                                    {page.heroMediaCaption}
                                                </figcaption>
                                            )}
                                        </figure>
                                    </div>
                                )}
                            </section>

                            {/* Aside - Mobile */}
                            <div
                                className='sm:hidden w-full'>
                                <ArticleSidebar page={page}/>
                            </div>

                            {/* Modules*/}
                            {page?.modules?.length > 0 && (
                                <div className='flex flex-col gap-32'>
                                    {page?.modules?.map((module, idx) => (
                                        <ModuleDispatcher
                                            {...module}
                                            key={module?.id + '-' + idx} />
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className='hidden sm:block w-full sm:w-3/12 md:w-4/12'>
                            <ArticleSidebar page={page}/>
                        </div>
                    </div>

                    {/* Related Events */}
                    {page.relatedItems?.edges?.length > 0 && (
                        <section className='bg-surface-panel-red mt-8 md:mt-16 rounded-lg py-24 pb-16 -mx-8 sm:-mx-8 md:-mx-16'>
                            <div className='px-4 sm:px-8 md:px-8'>
                                <div className='w-full col-padding'>
                                    <h3 className='type-style-sans-h1'>
                                        {'More like this'}
                                    </h3>
                                </div>
                            </div>
                            <Carousel
                                swiperConfig={{
                                    a11y: { enabled: true },
                                    slidesPerView: 1.2,
                                    slidesOffsetBefore: 8,
                                    slidesOffsetAfter: 8,
                                    breakpoints: {
                                        992: {
                                            slidesPerView: 2.35,
                                        },
                                        1440: {
                                            slidesPerView: 3.15,
                                            slidesOffsetBefore: 16,
                                            slidesOffsetAfter: 16
                                        }
                                    },
                                    spaceBetween: 8,
                                    grabCursor: false,
                                }}>
                                <div className='w-full col-padding flex justify-end mb-16 mt-20 md:mt-12'>
                                    <div className='px-4 md:px-8 flex'>
                                        <Button
                                            as='a'
                                            className='button-outcome'
                                            href={`${page?.parent?.url}?articleType=${page.articleTypes?.[0].slug}`}>
                                            {'View all'}
                                        </Button>
                                        {/* Desktop */}
                                        {page.relatedItems.edges?.length > 3 && (
                                            <div className='hidden md:block ml-8 md:ml-16'>
                                                <CarouselArrowNavigation />
                                            </div>
                                        )}
                                        {/* Tablet */}
                                        {page.relatedItems.edges?.length > 2 && (
                                            <div className='hidden sm:block md:hidden ml-8 md:ml-16'>
                                                <CarouselArrowNavigation />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <CarouselWrapper>
                                    {page.relatedItems.edges.map((item) => {
                                        if (!item.node) { return null; }

                                        const article = item.node;
                                        return (
                                            <SwiperSlide
                                                aria-roledescription='slide'
                                                className='select-none cursor-pointer flex flex-wrap mt-4'
                                                key={article.id}
                                                role='group'>
                                                <ArticleListingItem {...article} />
                                            </SwiperSlide>
                                        );
                                    }).filter(Boolean)}
                                </CarouselWrapper>
                            </Carousel>
                        </section>

                    )}
                </div>
            </main>

            <Footer
                {...props?.data?.footer}
                exploreTags={
                    page.tags?.length > 0 ?
                        { edges: page.tags.map(tag =>({ node: tag }))}
                        : props?.data?.footer?.exploreTags
                }
                newsletterHide={!page.showNewsletter}
                pageType={page.__typename} />
        </>
    );
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

export default ArticleDetailPage;
